"use client"

import { useRouter } from "@bprogress/next/app";
import { Box, Chip, colors, Divider, SxProps, Typography } from "@mui/material";
import { numberFormat } from "@v2/helpers/optimizedHelpers";
import { Trash2, X } from "lucide-react";
import { usePathname, useSearchParams } from "next/navigation";
import { MouseEvent, PointerEvent, useCallback, useMemo, useRef, useState } from "react";
import { PLPFilters, PLPSearchParams, PLPSpecialBrand, PLPSpecialCollection } from "../types";

type ActiveFilterChip = {
    key: string;
    label: string;
    params: string[];
    value?: string;
}

function toValues(value: string | string[] | undefined) {
    if (Array.isArray(value)) return value;
    if (!value) return [];
    return value.split(',').filter(Boolean);
}

function getLatestValue(value: string | string[] | undefined) {
    const values = toValues(value);
    return values[values.length - 1] ?? null;
}

export default function PlpActiveFilterChips({
    filters,
    searchParams,
    specialBrand,
    specialCollection,
    productDiscountTitle,
    sx,
}: {
    filters: PLPFilters;
    searchParams: PLPSearchParams;
    specialBrand: PLPSpecialBrand | null;
    specialCollection: PLPSpecialCollection | null;
    productDiscountTitle?: string | null;
    sx?: SxProps;
}) {
    const router = useRouter();
    const pathname = usePathname();
    const currentSearchParams = useSearchParams();
    const scrollerRef = useRef<HTMLDivElement>(null);
    const dragStateRef = useRef({
        isDragging: false,
        startX: 0,
        startScrollLeft: 0,
        didDrag: false,
        suppressClick: false,
    });
    const [isDragging, setIsDragging] = useState(false);

    const chips = useMemo<ActiveFilterChip[]>(() => {
        const output: ActiveFilterChip[] = [];

        // No chip for the search term: the heading above the list names it, and the way
        // out of a search is the back button, not clearing it here.

        const productClassSlug = getLatestValue(searchParams.product_class_slug);
        const productClass = filters.product_classes?.find((item) => item.slug === productClassSlug);
        if (productClass) {
            output.push({
                key: `product_class_slug:${productClass.slug}`,
                label: productClass.site_name,
                params: ['product_class_slug'],
            });
        }

        if (searchParams.brand_slug && specialBrand) {
            output.push({
                key: `brand_slug:${specialBrand.slug}`,
                label: specialBrand.name,
                params: ['brand_slug'],
            });
        }

        toValues(searchParams.brand).forEach((brandId) => {
            const brand = filters.brands.find((item) => String(item.id) === brandId);
            if (!brand) return;

            output.push({
                key: `brand:${brandId}`,
                label: brand.fa_name || brand.en_name,
                params: ['brand'],
                value: brandId,
            });
        });

        if (searchParams.collection_slug && specialCollection) {
            output.push({
                key: `collection_slug:${specialCollection.slug}`,
                label: specialCollection.title,
                params: ['collection_slug'],
            });
        }

        filters.attributes.forEach((filterAttribute) => {
            const searchParamsKey = `attr_${filterAttribute.attribute.id}`;
            toValues(searchParams[searchParamsKey]).forEach((value) => {
                const option = filterAttribute.values.find((item) => item.value === value);
                const labelValue = filterAttribute.attribute.type === "boolean"
                    ? value === "1" ? "بله" : "خیر"
                    : option?.value ?? value;
                output.push({
                    key: `${searchParamsKey}:${value}`,
                    label: `${filterAttribute.attribute.title}: ${labelValue}`,
                    params: [searchParamsKey],
                    value,
                });
            });
        });

        toValues(searchParams.fc).forEach((colorId) => {
            const color = filters.frame_colors?.find((item) => String(item.id) === colorId);
            if (!color) return;

            output.push({
                key: `fc:${colorId}`,
                label: `رنگ فریم: ${color.title}`,
                params: ['fc'],
                value: colorId,
            });
        });

        toValues(searchParams.lc).forEach((colorId) => {
            const color = filters.lens_colors?.find((item) => String(item.id) === colorId);
            if (!color) return;

            output.push({
                key: `lc:${colorId}`,
                label: `رنگ لنز: ${color.title}`,
                params: ['lc'],
                value: colorId,
            });
        });

        (filters.color_parts ?? []).forEach((part) => {
            const param = `cf_${part.slug}` as const;
            toValues(searchParams[param]).forEach((familySlug) => {
                const family = part.families.find((item) => item.slug === familySlug);
                if (!family) return;

                output.push({
                    key: `${param}:${familySlug}`,
                    label: `${part.filter_title}: ${family.title}`,
                    params: [param],
                    value: familySlug,
                });
            });
        });

        // A bound each, so neither runs past the chip width and either can be lifted alone.
        if (searchParams.minp) {
            output.push({
                key: `price:min:${searchParams.minp}`,
                label: `قیمت از ${numberFormat(Number(searchParams.minp))} تومان`,
                params: ['minp'],
            });
        }

        if (searchParams.maxp) {
            output.push({
                key: `price:max:${searchParams.maxp}`,
                label: `قیمت تا ${numberFormat(Number(searchParams.maxp))} تومان`,
                params: ['maxp'],
            });
        }

        if (searchParams.has_discount) {
            output.push({
                key: 'has_discount',
                label: 'تخفیف‌دارها',
                params: ['has_discount'],
            });
        }

        if (searchParams.has_quantity) {
            output.push({
                key: 'has_quantity',
                label: 'فقط کالاهای موجود',
                params: ['has_quantity'],
            });
        }

        if (searchParams.pd) {
            output.push({
                key: `pd:${searchParams.pd}`,
                label: productDiscountTitle ?? 'کمپین تخفیف',
                params: ['pd'],
            });
        }

        // Newest filter first. A key the user picks later is appended to the query while an
        // existing one keeps its slot, so the query order is the order the filters were added -
        // and inside one key the last value chosen sits last.
        const paramOrder = Object.keys(searchParams);
        const paramValues = searchParams as Record<string, string | string[] | undefined>;

        return output
            .map((chip, position) => ({ chip, position }))
            .sort((first, second) => {
                const keyDelta = paramOrder.indexOf(second.chip.params[0]) - paramOrder.indexOf(first.chip.params[0]);
                if (keyDelta) return keyDelta;

                const values = toValues(paramValues[first.chip.params[0]]);
                const valueDelta = values.indexOf(second.chip.value ?? '') - values.indexOf(first.chip.value ?? '');
                if (valueDelta) return valueDelta;

                return first.position - second.position;
            })
            .map((item) => item.chip);
    }, [filters, productDiscountTitle, searchParams, specialBrand, specialCollection]);

    const removeChip = useCallback((chip: ActiveFilterChip) => () => {
        const params = new URLSearchParams(currentSearchParams.toString());

        chip.params.forEach((param) => {
            if (!chip.value) {
                params.delete(param);
                return;
            }

            const currentValue = params.get(param) ?? searchParams[param];
            const nextValues = toValues(currentValue).filter((value) => value !== chip.value);
            if (nextValues.length) {
                params.set(param, nextValues.join(','));
            } else {
                params.delete(param);
            }
        });

        params.delete('page');
        params.delete('lp');

        const query = params.toString();
        router.push(`${pathname}${query ? `?${query}` : ''}`, { scroll: false });
    }, [currentSearchParams, pathname, router, searchParams]);

    const clearAllChips = useCallback(() => {
        const params = new URLSearchParams(currentSearchParams.toString());

        chips.forEach((chip) => {
            chip.params.forEach((param) => params.delete(param));
        });
        params.delete('page');
        params.delete('lp');

        const query = params.toString();
        router.push(`${pathname}${query ? `?${query}` : ''}`, { scroll: false });
    }, [chips, currentSearchParams, pathname, router]);

    const onClearAllClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        clearAllChips();
    }, [clearAllChips]);

    const stopChipDeletePointer = useCallback((event: PointerEvent<SVGSVGElement>) => {
        event.stopPropagation();
        dragStateRef.current.suppressClick = false;
    }, []);

    const onPointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
        if (!scrollerRef.current) return;

        dragStateRef.current = {
            isDragging: true,
            startX: event.clientX,
            startScrollLeft: scrollerRef.current.scrollLeft,
            didDrag: false,
            suppressClick: false,
        };

        scrollerRef.current.setPointerCapture(event.pointerId);
    }, []);

    const onPointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
        if (!dragStateRef.current.isDragging || !scrollerRef.current) return;

        const deltaX = event.clientX - dragStateRef.current.startX;
        if (Math.abs(deltaX) > 4) {
            dragStateRef.current.didDrag = true;
            setIsDragging(true);
        }

        scrollerRef.current.scrollLeft = dragStateRef.current.startScrollLeft - deltaX;
    }, []);

    const stopDragging = useCallback((event: PointerEvent<HTMLDivElement>) => {
        if (!dragStateRef.current.isDragging) return;

        dragStateRef.current.isDragging = false;
        dragStateRef.current.suppressClick = dragStateRef.current.didDrag;
        scrollerRef.current?.releasePointerCapture(event.pointerId);

        window.setTimeout(() => {
            setIsDragging(false);
            dragStateRef.current.didDrag = false;
        }, 0);
    }, []);

    const suppressClickAfterDrag = useCallback((event: MouseEvent<HTMLDivElement>) => {
        if (!dragStateRef.current.suppressClick) return;

        event.preventDefault();
        event.stopPropagation();
        dragStateRef.current.suppressClick = false;
    }, []);

    if (!chips.length) return null;

    return (
        <Box
            ref={scrollerRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={stopDragging}
            onPointerCancel={stopDragging}
            onClickCapture={suppressClickAfterDrag}
            sx={{
                maxWidth: '100%',
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollbarWidth: 'none',
                WebkitOverflowScrolling: 'touch',
                cursor: isDragging ? 'grabbing' : 'grab',
                userSelect: isDragging ? 'none' : 'auto',
                '&::-webkit-scrollbar': { display: 'none' },
                ...sx,
            }}
        >
            <Box sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: 1,
                alignItems: 'center',
                width: 'max-content',
                maxWidth: 'none',
                pointerEvents: isDragging ? 'none' : 'auto',
            }}>
                {chips.length > 1 && (
                    <>
                        <Chip
                            icon={<Trash2 size={14} />}
                            label="حذف همه"
                            onPointerDownCapture={(event) => {
                                event.stopPropagation();
                                dragStateRef.current.suppressClick = false;
                            }}
                            onClick={onClearAllClick}
                            variant="filled"
                            sx={{
                                height: 34,
                                // The filter button's corner: a chip here is a control like it,
                                // not one of the rounded tags that only describe a product.
                                borderRadius: '8px',
                                bgcolor: colors.red[50],
                                borderColor: colors.red[100],
                                color: colors.red[800],
                                fontWeight: 600,
                                cursor: 'pointer',
                                '& .MuiChip-label': {
                                    p: 0.5,
                                    pr: 1.5
                                },
                                '& .MuiChip-icon': {
                                    mr: 0.5,
                                    ml: 1.5,
                                    color: colors.red[700],
                                },
                                '&:hover': {
                                    bgcolor: colors.red[100],
                                },
                            }}
                        />
                        <Divider flexItem orientation="vertical" sx={{
                            my: 1
                        }} />
                    </>
                )}
                {chips.map((chip) => (
                    <Chip
                        key={chip.key}
                        label={<FilterChipLabel label={chip.label} />}
                        onDelete={removeChip(chip)}
                        deleteIcon={<X size={14} color={colors.red[700]} onPointerDownCapture={stopChipDeletePointer} />}
                        variant="outlined"
                        size="small"
                        sx={{
                            height: 34,
                            maxWidth: 260,
                            borderRadius: '8px',
                            bgcolor: colors.grey[50],
                            borderColor: colors.grey[200],
                            color: colors.grey[800],
                            '& .MuiChip-label': {
                                display: 'flex',
                                alignItems: 'center',
                                minWidth: 0,
                                pr: 0.5,
                                pl: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            },
                            '&:hover': {
                                '& .MuiChip-deleteIcon': {
                                    transform: 'scale(1.3)'
                                }
                            },
                            '& .MuiChip-deleteIcon': {
                                ml: 0.5,
                                mr: 0.75,
                                color: colors.grey[500],
                                bgcolor: colors.grey[200],
                                // A smaller square for the chip's square corners.
                                borderRadius: '4px',
                                p: '2px',
                                boxSizing: 'content-box',
                                transition: 'transform 0.2s ease-in-out',
                                '&:hover': {
                                    color: colors.grey[800],
                                    bgcolor: colors.grey[300],
                                },
                            },
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
}

function FilterChipLabel({ label }: { label: string }) {
    const separatorIndex = label.indexOf(':');

    if (separatorIndex === -1) {
        return (
            <Typography component="span" sx={{ fontSize: 12, fontWeight: 600 }}>
                {label}
            </Typography>
        );
    }

    return (
        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 300, color: colors.grey[800], whiteSpace: 'nowrap' }}>
                {label.slice(0, separatorIndex + 1)}
            </Typography>
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 500, color: colors.grey[900], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {label.slice(separatorIndex + 1).trim()}
            </Typography>
        </Box>
    );
}
